import { authAxios } from '@shared/api/auth-instance'
import { AxiosResponse } from 'axios'
import { FullEnrollmentView, ProgramWeek } from '../types/base'
import { ProgramEnrollment, ProgramTemplate } from '@entities/program/types/template'
import { URLS } from '@assets/environment/urls'
import { getCurrentBackendLocale } from '@shared/i18n'
import { Language } from '@shared/types/language'

type ProgramsListParams = {
	type: 'enrollments' | 'available'
	limit: number
	offset: number
	lang?: Language
	term?: string
}

type ProgramsListResponse = {
	results: number
	rows: ProgramTemplate[]
}

export class ProgramDataProxy {
	public static readonly BASE_URL: string = `${URLS.WORKOUT}programs`
	private static readonly PROGRAMS_PAGE_SIZE = 30

	public static enroll = async (
		programTemplateId: number
	): Promise<null | FullEnrollmentView> => {
		const urlParams: URLSearchParams = new URLSearchParams()
		urlParams.set('program_template_id', programTemplateId.toString())

		try {
			const response: AxiosResponse<FullEnrollmentView> = await authAxios.post(
				this.BASE_URL + '/enroll',
				{},
				{ params: urlParams }
			)
			return response.data
		} catch (e) {
			console.error('Ошибка при записи на программу:', e)
			return null
		}
	}

	public static getProgramsPage = async (
		params: ProgramsListParams
	): Promise<null | ProgramsListResponse> => {
		const urlParams: URLSearchParams = new URLSearchParams()
		urlParams.set('limit', params.limit.toString())
		urlParams.set('offset', params.offset.toString())

		if (params.lang) {
			urlParams.set('lang', params.lang)
		}

		if (params.term) {
			urlParams.set('term', params.term)
		}

		try {
			const response: AxiosResponse<ProgramsListResponse> = await authAxios.get(
				this.BASE_URL + `/${params.type}`,
				{ params: urlParams }
			)
			return response.data
		} catch (e: unknown) {
			console.error('Ошибка при получении шаблонов программ', e)
			return null
		}
	}

	public static getProgramsList = async (
		params: ProgramsListParams
	): Promise<null | ProgramTemplate[]> => {
		const response = await this.getProgramsPage(params)
		return response?.rows ?? null
	}

	public static getAllProgramsList = async (
		params: Omit<ProgramsListParams, 'limit' | 'offset'> & {
			limit?: number
			offset?: number
		}
	): Promise<null | ProgramTemplate[]> => {
		const limit = params.limit ?? this.PROGRAMS_PAGE_SIZE
		let offset = params.offset ?? 0
		let total: number | null = null
		const rows: ProgramTemplate[] = []

		while (total === null || rows.length < total) {
			const response = await this.getProgramsPage({
				...params,
				limit,
				offset,
			})

			if (!response) {
				return null
			}

			total = response.results

			if (response.rows.length === 0) {
				break
			}

			rows.push(...response.rows)
			offset += response.rows.length
		}

		return rows
	}

	public static getSuggestedProgram = async (
		params: { lang?: Language } = {}
	): Promise<null | ProgramTemplate> => {
		const lang = params.lang ?? getCurrentBackendLocale()
		const lastStartedProgram = await this.getProgramsList({
			type: 'enrollments',
			limit: 1,
			offset: 0,
			lang,
			term: '',
		})
		if (lastStartedProgram && lastStartedProgram.length === 1) {
			return lastStartedProgram[0]
		}
		const suggestedEnrollment = await this.getProgramsList({
			type: 'available',
			lang,
			term: '',
			offset: 0,
			limit: 1,
		})
		if (suggestedEnrollment && suggestedEnrollment.length === 1) {
			return suggestedEnrollment[0]
		}
		return null
	}

	public static getEnrollmentById = async (
		enrollmentId: number,
		lang: Language = getCurrentBackendLocale()
	): Promise<ProgramEnrollment | null> => {
		try {
			const result: AxiosResponse<ProgramEnrollment> = await authAxios.get(
				this.BASE_URL + `/enrollments/${enrollmentId}`,
				{ params: { lang } }
			)

			return result.data
		} catch (e: unknown) {
			console.error('Not available to find enrollment by id', e)
			return null
		}
	}

	public static makeProgramFavorite = async (programTemplateId: number): Promise<boolean> => {
		const urlParams: URLSearchParams = new URLSearchParams()
		urlParams.set('program_template_id', programTemplateId.toString())

		try {
			const response: AxiosResponse<{ ok: boolean }> = await authAxios.post(
				this.BASE_URL + '/favorites',
				{},
				{ params: urlParams }
			)
			return response.data.ok
		} catch (e: unknown) {
			console.log('Erorr while adding program to favorites', e)
			return false
		}
	}

	public static getFavoriteProgramsList = async (
		limit: number,
		offset: number
	): Promise<ProgramTemplate[] | null> => {
		const urlParams: URLSearchParams = new URLSearchParams()
		urlParams.set('limit', limit.toString())
		urlParams.set('offset', offset.toString())

		try {
			const response: AxiosResponse<ProgramTemplate[]> = await authAxios.get(
				this.BASE_URL + '/favorites',
				{ params: urlParams }
			)
			return response.data
		} catch (e: unknown) {
			console.error('Ошибка при получении списка избранных программ:', e)
			return null
		}
	}

	public static getWeekInfo = async (
		enrollmentId: string,
		weekNum: string
	): Promise<ProgramWeek | null> => {
		try {
			const response: AxiosResponse<ProgramWeek> = await authAxios.get(
				`${this.BASE_URL}/${enrollmentId}/weeks/${weekNum}`
			)
			return response.data
		} catch (e: unknown) {
			console.error('Ошибка в получении недели программы: ', e)
			return null
		}
	}
}
